import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { Loader2, Plus } from "lucide-react";
import { AccountCard } from "@/components/account/AccountCard";
import { CreateAccountDialog } from "@/components/account/CreateAccountDialog";
import { QRDialog } from "@/components/account/QRDialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  AccountsProvider,
  useAccounts,
} from "@/components/account/AccountsContext";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { apiService } from "@/services/api";

const AccountsList = () => {
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showQRDialog, setShowQRDialog] = useState(false);
  const [qrCodeImage, setQrCodeImage] = useState<string | null>(null);
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(
    null
  );
  const { toast } = useToast();
  const { t } = useTranslation();
  const {
    accounts,
    isLoading: isLoadingAccounts,
    createAccount,
    loginAccount,
    logoutAccount,
    syncAccount,
    updatePrivacyMode,
    contactsMetadata,
  } = useAccounts();
  const { data: user, isLoading: isLoadingUser } = useQuery({
    queryKey: ["user"],
    queryFn: async () => {
      const response = await apiService.getCurrentUser();
      return response.data;
    },
  });
  const accountsLimitReached = useMemo(() => {
    return accounts?.length && accounts?.length >= (user?.settings?.accounts_limit || 0);
  }, [accounts, user]);

  useEffect(() => {
    if (accountsLimitReached) {
      console.log(
        "Accounts limit reached. Cannot create new accounts."
      );
    }
  }, [accountsLimitReached]);

  const handleCreateAccount = async () => {
    try {
      const result = await createAccount();
      setSelectedAccountId(result.id);
      setQrCodeImage(result.qrImage);
      setShowCreateDialog(false);
      setShowQRDialog(true);
      toast({
        title: t("common.success"),
        description: t("accounts.create_success"),
      });
    } catch (error) {
      toast({
        title: t("common.error"),
        description: t("accounts.create_error"),
        variant: "destructive",
      });
    }
  };

  const handleLogin = async (id: string) => {
    try {
      const result = await loginAccount(id);
      setQrCodeImage(result.image);
      setSelectedAccountId(id);
      setShowQRDialog(true);
    } catch (error) {
      toast({
        title: t("common.error"),
        description: t("accounts.qr_error"),
        variant: "destructive",
      });
    }
  };

  const handleQRDialogClose = () => {
    if (selectedAccountId) {
      syncAccount(selectedAccountId);
    }
    setSelectedAccountId(null);
    setQrCodeImage(null);
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold">{t("pages.accounts.title")}</h1>
        <TooltipProvider>
          {accountsLimitReached ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button
                    onClick={() => setShowCreateDialog(true)}
                    disabled
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    {t("accounts.add_account")}
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>
                {t("accounts.accounts_limit_reached")}
              </TooltipContent>
            </Tooltip>
          ) : (
            <span>
              <Button
                onClick={() => setShowCreateDialog(true)}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                disabled={isLoadingUser || isLoadingAccounts}
              >
                <Plus className="mr-2 h-4 w-4" />
                {t("accounts.add_account")}
              </Button>
            </span>
          )}
        </TooltipProvider>
      </div>

      {isLoadingAccounts ? (
        <div className="flex justify-center">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {accounts?.map((account) => (
            <AccountCard
              key={account.id}
              account={account}
              onLogin={handleLogin}
              onLogout={logoutAccount}
              onSync={syncAccount}
              onPrivacyModeChange={updatePrivacyMode}
            />
          ))}
        </div>
      )}

      <CreateAccountDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        onSubmit={handleCreateAccount}
        isLoading={false}
      />

      <QRDialog
        open={showQRDialog}
        onOpenChange={setShowQRDialog}
        qrCodeImage={qrCodeImage}
        onSync={() => selectedAccountId && syncAccount(selectedAccountId)}
        onClose={handleQRDialogClose}
      />
    </div>
  );
};

const Accounts = () => {
  return (
    <AccountsProvider>
      <AccountsList />
    </AccountsProvider>
  );
};

export default Accounts;
